import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Mail, X, ImagePlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { registerFeedbackOpener } from '../../utils/feedbackOpener';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024;

type PageContext = {
  path: string;
  url: string;
  title: string;
};

export const FeedbackFab = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, userData } = useAuth();
  const [open, setOpen] = useState(false);
  const [pageCtx, setPageCtx] = useState<PageContext | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector<HTMLElement>('input:not([type="file"])')
        ?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const openModal = useCallback(() => {
    setPageCtx({
      path: `${location.pathname}${location.search}${location.hash}`,
      url: typeof window !== 'undefined' ? window.location.href : '',
      title: typeof document !== 'undefined' ? document.title : '',
    });
    setEmail(user?.email ?? '');
    setName(userData?.displayName?.trim() || '');
    setMessage('');
    setFiles([]);
    setOpen(true);
  }, [
    location.hash,
    location.pathname,
    location.search,
    user,
    userData?.displayName,
  ]);

  useEffect(() => {
    registerFeedbackOpener(openModal);
    return () => registerFeedbackOpener(null);
  }, [openModal]);

  const onPickFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const next: File[] = [...files];
    for (let i = 0; i < list.length; i++) {
      if (next.length >= MAX_FILES) {
        toast.error(t('feedbackForm.maxFiles', { count: MAX_FILES }));
        break;
      }
      const f = list[i];
      if (!f.type.startsWith('image/')) {
        toast.error(t('feedbackForm.imagesOnly'));
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(t('feedbackForm.imageTooLarge', { mb: 5 }));
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error(t('errors.auth'));
      return;
    }
    const ctx = pageCtx ?? {
      path: `${location.pathname}${location.search}${location.hash}`,
      url: window.location.href,
      title: document.title,
    };
    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      const fd = new FormData();
      fd.append('email', email.trim());
      fd.append('name', name.trim());
      fd.append('message', message.trim());
      fd.append('pagePath', ctx.path);
      fd.append('pageUrl', ctx.url);
      fd.append('pageTitle', ctx.title);
      fd.append('userAgent', navigator.userAgent);
      files.forEach((f) => fd.append('attachments', f));
      await axios.post(`${API_URL}/feedback`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(t('feedbackForm.success'));
      setOpen(false);
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : t('errors.generic');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        title={t('feedbackForm.fabTitle')}
        aria-label={t('feedbackForm.fabAria')}
        className="fixed bottom-6 right-6 z-[100] flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg ring-2 ring-white transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <Mail className="h-7 w-7" strokeWidth={1.75} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[110] flex items-end justify-center sm:items-center sm:p-4"
          role="presentation"
        >
          <button
            type="button"
            aria-label={t('common.close')}
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-form-title"
            className="relative z-10 flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h2
                id="feedback-form-title"
                className="text-lg font-semibold text-gray-900"
              >
                {t('feedbackForm.title')}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                aria-label={t('common.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4"
            >
              {pageCtx && (
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  <span className="font-medium text-gray-700">
                    {t('feedbackForm.pageLabel')}
                  </span>{' '}
                  <span className="break-all">{pageCtx.path}</span>
                </div>
              )}

              <div>
                <label
                  htmlFor="feedback-email"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  {t('feedbackForm.email')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label
                  htmlFor="feedback-name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  {t('feedbackForm.name')}
                </label>
                <input
                  id="feedback-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div className="min-h-0 flex-1">
                <label
                  htmlFor="feedback-message"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  {t('feedbackForm.message')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="feedback-message"
                  required
                  minLength={5}
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder={t('feedbackForm.messagePlaceholder')}
                />
              </div>

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_IMAGES}
                  multiple
                  className="hidden"
                  onChange={(e) => onPickFiles(e.target.files)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={files.length >= MAX_FILES}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ImagePlus className="h-4 w-4" />
                  {t('feedbackForm.addImages')}
                </button>
                <p className="mt-1 text-xs text-gray-500">
                  {t('feedbackForm.imagesHint', { max: MAX_FILES, mb: 5 })}
                </p>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-2">
                    {files.map((f, i) => (
                      <li
                        key={`${f.name}-${i}`}
                        className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5 text-xs text-gray-700"
                      >
                        <span className="truncate">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="shrink-0 text-red-600 hover:underline"
                        >
                          {t('common.delete')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="sticky bottom-0 flex gap-3 border-t border-gray-100 bg-white pt-3 pb-1">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  {submitting ? t('common.loading') : t('feedbackForm.send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
